/**
 * Theme profiles (#190) — named palettes for the app's *interface*.
 *
 * A profile restyles the chrome, the highlight / selection colors, the
 * construction guides and rays, the drag previews and the cut marks. It does
 * NOT touch the material: `--leather-*` stays the fixed veg-tan tone in every
 * profile, hardware finishes stay in `HardwareCatalog.ts`, and the CAM export
 * colors keep their machine meaning. Simulating dyed or painted leather is a
 * separate job for a later ticket — a profile changes how the app looks, not
 * what the piece is made of.
 *
 * Each profile draws its palette from a family of colors a leatherworker
 * already recognizes, which is where the names come from.
 *
 * The values themselves live in `src/styles.css` (per the AGENTS.md rule that
 * every display color is a custom property). This module owns the *contract*:
 * which profiles exist, and which variables a profile is allowed and required
 * to redefine. `themeProfiles.test.ts` parses the stylesheet and holds it to
 * exactly that contract, so a profile can't silently miss a variable or reach
 * into the material palette.
 */

/** The id written to `<html data-theme="...">`. "Plain" is the absence of the
 *  attribute — the same trick `colorModePref` uses for "system" — so the
 *  default look needs no block of its own and no magic string. */
export type ThemeProfileId =
  | "acrylic"
  | "dye"
  | "leather"
  | "thread"
  | "hardware";

export interface ThemeProfile {
  id: ThemeProfileId;
  /** Shown in the Settings selector. */
  label: string;
  /** One line of "where these colors come from", shown under the selector. */
  hint: string;
}

/** Label for the no-profile default. Not a `ThemeProfileId` — selecting it
 *  clears the preference rather than storing a value. */
export const PLAIN_PROFILE_LABEL = "Plain";

export const THEME_PROFILES: readonly ThemeProfile[] = [
  {
    id: "acrylic",
    label: "Acrylic Paint",
    hint: "Acrylic leather paint — turquoise and gold against near-white.",
  },
  {
    id: "dye",
    label: "Leather Dyes",
    hint: "Leather dye — oxblood, tan and deep browns.",
  },
  {
    id: "leather",
    label: "Famous Leathers",
    hint: "Shell cordovan and whiskey — amber over warm neutrals.",
  },
  {
    id: "thread",
    label: "Thread Colors",
    hint: "Waxed braided thread — navy, thread-yellow and spool red.",
  },
  {
    id: "hardware",
    label: "Hardware",
    hint: "Solid brass and gun metal — gold on cool grey.",
  },
];

export const THEME_PROFILE_IDS: readonly ThemeProfileId[] = THEME_PROFILES.map(
  (p) => p.id,
);

/**
 * Chrome variables. These already differ between light and dark in the base
 * theme, so a profile has to supply BOTH — otherwise switching to dark under a
 * profile would fall back to the plain chrome and the profile would evaporate
 * at exactly the moment it matters most.
 */
export const THEMED_CHROME_VARS: readonly string[] = [
  "--bg",
  "--panel",
  "--panel-2",
  "--border",
  "--text",
  "--text-dim",
  "--accent",
  "--accent-2",
  "--accent-contrast",
  "--danger",
  "--canvas-bg",
];

/**
 * Canvas-side variables a profile owns: selection highlights, snap dots,
 * construction guides, drag previews, the compass gauge and the red cut marks.
 *
 * Mode-independent by design — the base theme deliberately keeps everything
 * drawn on the canvas identical in light and dark so a piece looks the same
 * either way, and profiles keep that promise.
 */
export const THEMED_CANVAS_VARS: readonly string[] = [
  "--canvas-accent",
  "--canvas-accent-soft",
  "--canvas-accent-leather",
  "--canvas-snap-candidate",
  "--construction-stroke",
  "--construction-halo",
  "--construction-preview",
  "--construction-broken",
  "--preview-fill",
  "--preview-freehand",
  "--compass-active",
  "--compass-axis",
  "--constraint-icon-dot",
  "--piece-cut",
];

/**
 * Variables a profile must NEVER redefine.
 *
 * - `--leather-*` is the material. It is the same veg-tan in every profile and
 *   in both color modes; changing it is the future "simulate other materials"
 *   feature, not this one.
 * - `--mat-*` is the cutting mat. The working surface reads the same green
 *   whatever the interface is wearing.
 * - `--export-*` carries machine meaning (red cut / blue hole / magenta stitch
 *   is a laser convention, not a taste). A themed export would silently change
 *   what a cutter does with the file.
 */
export const FIXED_VARS: readonly string[] = [
  "--leather-fill",
  "--leather-edge",
  "--leather-border",
  "--leather-decor",
  "--mat-bg",
  "--mat-grid-fine",
  "--mat-grid-coarse",
  "--mat-axis",
  "--export-cut",
  "--export-hole",
  "--export-stitch",
  "--export-engrave",
  "--export-decor",
  "--export-construction",
  "--export-raster-bg",
];

/** Look a profile up by id. Returns null for an unknown id so a stale
 *  preference from an older build degrades to Plain instead of throwing. */
export function themeProfileById(id: string | null): ThemeProfile | null {
  if (id === null) return null;
  return THEME_PROFILES.find((p) => p.id === id) ?? null;
}
